import { h } from 'preact';
import { useMemo, useState } from 'preact/hooks';
import { ChevronRightIcon, CloseIcon, DownloadIcon, LoaderIcon } from '@uw/icons';
import { compactNumberFormat } from '@uw/utils';
import { useTranslation, useViolationsReport } from 'hooks';
import { widgetScreensRoutingStore } from 'global/widget-screens-routing.store';
import { stateStore } from 'global/state.store';
import { sessionStore } from 'global/session.store';
import { IssueType, CHART_COLOR } from 'constants/violations';
import { useViolations } from 'contexts/use-violations';
import { getChartData } from 'helpers/get-chart-data';
import './violations.scss';

const valueWithCommas = (value: number) => value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const Violations = () => {
  const { translate } = useTranslation();
  const { fetchingViolationsInProgress, foundViolations } = useViolations();
  const { downloadPdfReport, isReportLoading } = useViolationsReport();

  const [hideViolations, setHideViolations] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const user = sessionStore.getUser();

  const chart = useMemo(() => {
    if (foundViolations) {
      return getChartData(foundViolations, CHART_COLOR.red);
    }
  }, [foundViolations]);

  const goToRemediation = () => {
    widgetScreensRoutingStore.setPerSiteSettingsFlowThisSite$.next();
    widgetScreensRoutingStore.updateState$.next({
      path: 'siteRemediationUpgradeView',
      value: 'main.logged.dashboard',
    });
    stateStore.state.next('main.logged.site_remediation_upgrade');
  };

  if (fetchingViolationsInProgress || foundViolations.totalCount < 1) {
    return null;
  }

  return (
    <div
      className={`uwaw-violations uwaw-violations_initial ${
        hideViolations ? 'uwaw-violations_hide' : ''
      }`}
    >
      <div className="uwaw-violations__i">
        <p className="uwaw-violations__title">
          <strong>{user?.first_name}</strong>, {translate('widget.violations.initial_question')}
        </p>
        <p className="uwaw-violations__title">{translate('widget.violations.visible_info')}</p>

        <button
          type="button"
          className="uwaw-violations__close"
          onClick={() => setHideViolations(true)}
        >
          <CloseIcon />
        </button>

        <button
          type="button"
          className="uwaw-violations__info"
          onClick={() => {
            setShowDetails(!showDetails);
          }}
        >
          <div
            className="uwaw-violations__chart"
            style={{ background: chart.background }}
            role="img"
            aria-label={chart.label}
          >
            {chart.rotates.map((rotate) => (
              <span key={rotate} className="uwaw-violations__chart__separator" style={{ rotate }} />
            ))}
            <div className="uwaw-violations__chart__i">
              {compactNumberFormat(foundViolations.totalCount)}
            </div>
          </div>
          <div
            className="uwaw-violations__chart__title"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: translate('widget.violations.chart_title') }}
          />

          <span
            className={`uwaw-violations__drop-icon ${
              showDetails ? 'uwaw-violations__drop-icon_active' : ''
            }`}
          >
            <ChevronRightIcon />
          </span>
        </button>

        {showDetails && (
          <div className="uwaw-violations__main">
            <ul className="uwaw-violations__data">
              {Object.values(IssueType).map((type) => (
                <li className="uwaw-violations__item" key={type}>
                  <div className="uwaw-violations__item__issue-type">
                    <div
                      className={`uwaw-violations__item__lvl uwaw-violations__item__lvl_${type.toLowerCase()}`}
                    >
                      {type}
                    </div>
                    <div className="uwaw-violations__item__value">
                      {compactNumberFormat(foundViolations[type].count)}
                    </div>
                  </div>
                  <div className="uwaw-shortcut-tooltip">
                    <div className="uwaw-shortcut-tooltip__i">
                      <p className="uwaw-shortcut-tooltip__title-value">
                        {valueWithCommas(foundViolations[type].count)}
                      </p>
                      <p className="uwaw-shortcut-tooltip__title">
                        {' '}
                        {translate('widget.remediation.violations.tooltip_title')}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="uwaw-violations__btns">
              <button
                onClick={() => downloadPdfReport()}
                type="button"
                className="uwaw-btn uwaw-btn_border"
              >
                {isReportLoading ? <LoaderIcon /> : <DownloadIcon />}
                {translate('widget.violations.report_button.label')}
              </button>
              <button onClick={goToRemediation} type="button" className="uwaw-btn uwaw-btn_blue">
                {translate('widget.violations.fix_button.label')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
